import { useState } from "react"
import { Card, Button } from "antd"
import RadarDisplay from "../RadarDisplay"
import { X } from "lucide-react"

const LiveRadarSignal = ({ id, description, score, signal_type, timestamp, onRemove }) => {
  const getRemainingTime = (delay) => {
    switch (delay) {
      case "1 hour":
        return "1h remaining"
      case "6 hours":
        return "6h remaining"
      case "1 day":
        return "1d remaining"
      case "3 days":
        return "3d remaining"
      case "7 days":
        return "7d remaining"
      default:
        return ""
    }
  }

  const getSignalColorClass = (signalType) => {
    const cleanedType = signalType.replace(/\*\*/g, "")
    if (cleanedType.includes("Risk")) return "text-red-500"
    if (cleanedType.includes("Opp")) return "text-green-500"
    return "text-blue-500"
  }

  const calculateSignalAge = (signalTimestamp) => {
    const now = new Date()
    const signalTime = new Date(signalTimestamp)
    const diffMs = now.getTime() - signalTime.getTime()
    const diffMinutes = Math.round(diffMs / (1000 * 60))
    const diffHours = Math.round(diffMs / (1000 * 60 * 60))
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24))

    if (diffMinutes < 1) return "just now"
    if (diffMinutes < 60) return `${diffMinutes}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    return `${diffDays}d ago`
  }

  return (
    <div className="relative flex flex-col p-3 border border-gray-200 rounded-md bg-white shadow-sm">
      <button onClick={() => onRemove(id)} className="absolute top-1 right-1 text-gray-400 hover:text-gray-600 text-lg">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-center gap-2 text-sm font-semibold mb-1">
        <span className={`${getSignalColorClass(signal_type)}`}>{signal_type.includes("Risk") ? "🟧" : "🟩"}</span>
        <span className="truncate">{description}</span>
      </div>
      <div className="grid grid-cols-2 gap-1 text-xs">
        <div>
          Score: <span>{score}</span>
        </div>
        <div>
          Age: <span>{calculateSignalAge(timestamp)}</span>
        </div>
      </div>
    </div>
  )
}

export default function LiveRadarExecutionPanel({
  logs,
  radarPriorities,
  confidenceThreshold,
  executionScoreThreshold,
  executionDelayBuffer,
  isLoading,
}) {
  const [selectedRadarSignal, setSelectedRadarSignal] = useState(null)
  const getSignalFillColor = (score) => {
     if (score >= 85) {
      return "text-red-400"
    } else if (score >= 70) {
      return "text-orange-400"
    } else if (score >= 50) {
      return "text-yellow-400"
    } else {
      return "text-green-400"
    }
  }

  const cleanSignal = (signal) => {
    return signal.replace(/^\*+\s*"?|"?$/g, "").trim() || null;
  }
  const cleanEnrichment = (signal) => {
    return signal.replace(/\*\*.*?\*\*:?/g, '').trim() || signal;
  }

  return (
    <>
      {isLoading ? (
        <div className="w-full h-screen flex items-center justify-center bg-gray-100">
          <div className="text-lg">Loading...</div>
        </div>
      ) : (
        <Card
          bordered={false}
          headStyle={{ borderBottom: "1px solid #e5e7eb", padding: "16px 16px 12px 16px" }}
          bodyStyle={{ padding: 0, display: "flex", flexDirection: "column", flex: 1 }}
          title={<div className="text-sm">LIVE RADAR + EXECUTION PANEL</div>}
          className="w-full h-full flex flex-col"
        >
          <div className="flex-1 min-h-[200px] relative">
            <RadarDisplay
              logs={logs}
              radarPriorities={radarPriorities}
              confidenceThreshold={confidenceThreshold}
              executionScoreThreshold={executionScoreThreshold}
              executionDelayBuffer={executionDelayBuffer}
              onSignalClick={setSelectedRadarSignal}
            />
          </div>

          {/* New section for clicked signal details */}
          {selectedRadarSignal && (
            <div className="p-4 border-t border-gray-200 bg-white">
              <div className="flex justify-between">
                <h3 className="text-sm font-bold mb-2">SIGNAL DETAILS</h3>
                <X className="w-4 h-4 cursor-pointer" onClick={() => setSelectedRadarSignal(null)} />
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Type:</span>
                  <span className={`${getSignalFillColor(selectedRadarSignal.score)} font-medium`}>
                    {selectedRadarSignal.signal_type}
                  </span>
                </div>
                <div>
                  <span className="font-semibold">Raw Signal:</span>
                  <p className="text-xs text-gray-700">{cleanSignal(selectedRadarSignal.raw_signal)}</p>
                </div>
                <div>
                  <span className="font-semibold">Enrichment:</span>
                  <p className="text-xs text-gray-700">{cleanEnrichment(selectedRadarSignal.enrichment)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Score:</span>
                  <span className="text-gray-700">{selectedRadarSignal.score}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Decision:</span>
                  <span className="text-gray-700">{selectedRadarSignal.decision}</span>
                </div>
              </div>
            </div>
          )}

          <div className="p-4 flex-shrink-0">
            <Button type="primary" className="w-full bg-gray-800 text-white hover:bg-gray-700">
              EXECUTE ALL
            </Button>
          </div>
        </Card>
      )}
    </>
  )
}
